// Home.js
import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { useNavigate } from 'react-router-dom';

import beerLoader from './images/animations/beerLoader.gif';
import beerMug from './images/assets/beerMug.svg';
import PasswordService from './fragments/PasswordService';
import ActionService from './fragments/ActionService';
import InfoPopup from './fragments/InfoPopup';
import expandIcon from './images/assets/expand.svg';
import contractIcon from './images/assets/contract.svg';
import successIcon from './images/assets/success.svg';
import warningIcon from './images/assets/warning.svg';
import errorIcon from './images/assets/fail.svg';
import cancelledIcon from './images/assets/Cancelled.svg';
import paidIcon from './images/assets/Paid.svg';
import unpaidIcon from './images/assets/Unpaid.svg';
import Pagination from './components/Pagination';

function Home() {
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState([]);
    const [message, setMessage] = useState('');
    const [products, setProducts] = useState([]);
    const [activeProducts, setActiveProducts] = useState([]);
    const [productHistory, setProductHistory] = useState([]);
    const [openOrders, setOpenOrders] = useState([]);
    const [orderHistory, setOrderHistory] = useState([]);
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [transactionsLoading, setTransactionsLoading] = useState(false);

    // Auth Context
    const {
        loggedIn,
        setLoggedIn,
        userName,
        setUserName,        // ← Used here to update header
        adminUser,
        setAdminUser,
        viewedUser,
        setViewedUser,
        isFirstTime,
        setIsFirstTime,
        loggedUser,
        setLoggedUser
    } = useContext(AuthContext);

    // UI States
    const [serviceConfirmVisible, setServiceConfirmVisible] = useState(false);
    const [passwordServiceVisible, setPasswordServiceVisible] = useState(isFirstTime);
    const [infoPopupVisible, setInfoPopupVisible] = useState(false);
    const [popupTitle, setPopupTitle] = useState(null);
    const [popupMessage, setPopupMessage] = useState(null);
    const [popupIcon, setPopupIcon] = useState(null);
    const [serviceToAction, setServiceToAction] = useState(null);
    const [actionType, setActionType] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [cart, setCart] = useState([]);
    const [enrollmentAck, setEnrollmentAck] = useState(false);
    const [expandedHistory, setExpandedHistory] = useState({});
    const [expandedItems, setExpandedItems] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);

    // Pagination
    const ORDER_PAGE_SIZE = 10;
    const TRANSACTION_PAGE_SIZE = 10;

    const [orderPage, setOrderPage] = useState(1);
    const [orderPagination, setOrderPagination] = useState({
        currentPage: 1,
        pageSize: ORDER_PAGE_SIZE,
        totalItems: 0,
        totalPages: 1
    });

    const [transactionPage, setTransactionPage] = useState(1);
    const [transactionPagination, setTransactionPagination] = useState({
        currentPage: 1,
        pageSize: TRANSACTION_PAGE_SIZE,
        totalItems: 0,
        totalPages: 1
    });

    // ====================== FETCH USER (Session Check) ======================
    const fetchUser = async () => {
        try {
            const { data } = await axios.get('/api/check-session', { withCredentials: true });
            if (data.loggedIn) {
                setLoggedIn(true);
                if (!adminUser) {
                    setUser(data.user);
                }
            } else {
                setAdminUser(false);
                setLoggedIn(false);
                navigate('/');
            }
        } catch (error) {
            console.error('Session check failed:', error);
            setLoggedIn(false);
            navigate('/');
        }
    };

    // ====================== MAIN DATA FETCH ======================
    const fetchData = async (userId) => {
        if (!userId) return;

        try {
            setIsLoading(true);

            const [userDataRes, activeProductsData, availableProductsData, openOrdersData, orderHistoryData, transactionHistoryData] =
                await Promise.all([
                    fetch(`/api/userData?userId=${userId}`).then(res => res.json()),
                    fetch(`/api/activeProducts?userId=${userId}&date=${new Date().toISOString().split('T')[0]}`).then(res => res.json()),
                    fetch(`/api/products?userId=${userId}&date=${new Date().toISOString().split('T')[0]}`).then(res => res.json()),
                    fetch(`/api/orders?userId=${userId}`).then(res => res.json()),
                    fetchOrderHistory(userId, 1, ORDER_PAGE_SIZE, searchQuery),
                    fetchTransactionHistory(userId, 1, TRANSACTION_PAGE_SIZE)
                ]);

            setUserData(userDataRes);

            // IMPORTANT FIX: Update context so header shows correct name
            if (userDataRes?.user_name) {
                setUserName(userDataRes.user_name);
            }

            setActiveProducts(activeProductsData);
            setOpenOrders(openOrdersData);
            setOrderHistory(orderHistoryData.data || []);
            setOrderPagination(orderHistoryData.pagination || {
                currentPage: 1,
                pageSize: ORDER_PAGE_SIZE,
                totalItems: 0,
                totalPages: 1
            });
            setTransactionHistory(transactionHistoryData.data || []);
            setTransactionPagination(transactionHistoryData.pagination || {
                currentPage: 1,
                pageSize: TRANSACTION_PAGE_SIZE,
                totalItems: 0,
                totalPages: 1
            });

            // Filter available products (not already active)
            const filteredAvailableProducts = availableProductsData.filter(product =>
                !activeProductsData.some(p => p.product_id === product.product_id)
            );
            setProducts(filteredAvailableProducts);

        } catch (error) {
            console.error('Data fetch error:', error);
            setMessage('Error loading data');
        } finally {
            setIsLoading(false);
        }
    };

    // ====================== SINGLE MAIN FETCHING EFFECT ======================
    useEffect(() => {
        fetchUser();
    }, []); // Run only once on mount

    useEffect(() => {
        if (!loggedIn) {
            setIsLoading(false);
            return;
        }

        const userId = adminUser && viewedUser ? viewedUser.user_id : user?.id;

        if (!userId) {
            setMessage(adminUser ? 'Select a user to view their data.' : 'Unable to load user data.');
            setIsLoading(false);
            return;
        }

        // Prevent unnecessary re-fetch when returning via browser back button
        if (userData?.user_id === userId && !isLoading) {
            setIsLoading(false);
            return;
        }

        fetchData(userId);
    }, [loggedIn, adminUser, viewedUser?.user_id, user?.id]);

    // ====================== ORDER & TRANSACTION HISTORY EFFECTS ======================
    useEffect(() => {
        if (!user && !viewedUser) return;

        const userId = adminUser ? viewedUser?.user_id : user?.id;
        if (!userId) return;

        const loadOrderHistory = async () => {
            try {
                setOrdersLoading(true);
                const result = await fetchOrderHistory(userId, orderPage, ORDER_PAGE_SIZE, searchQuery);
                setOrderHistory(result.data || []);
                setOrderPagination(result.pagination || { totalItems: 0, totalPages: 1 });
            } catch (err) {
                console.error("Order history fetch failed", err);
            } finally {
                setOrdersLoading(false);
            }
        };

        loadOrderHistory();
    }, [orderPage, searchQuery, user?.id, viewedUser?.user_id, adminUser]);

    useEffect(() => {
        if (!user && !viewedUser) return;

        const userId = adminUser ? viewedUser?.user_id : user?.id;
        if (!userId) return;

        const loadTransactionHistory = async () => {
            try {
                setTransactionsLoading(true);
                const result = await fetchTransactionHistory(userId, transactionPage, TRANSACTION_PAGE_SIZE);
                setTransactionHistory(result.data || []);
                setTransactionPagination(result.pagination || { totalItems: 0, totalPages: 1 });
            } catch (err) {
                console.error("Transaction history fetch failed", err);
            } finally {
                setTransactionsLoading(false);
            }
        };

        loadTransactionHistory();
    }, [transactionPage, user?.id, viewedUser?.user_id, adminUser]);

    // ====================== AUTO-SELECT ALL OPEN ORDERS ======================
    useEffect(() => {
        if (openOrders && openOrders.length > 0) {
            const allSalesOrders = groupOrdersBySalesOrder(openOrders).map(order => order.sales_order);
            setSelectedOrders(allSalesOrders);

            const newCart = groupOrdersBySalesOrder(openOrders)
                .flatMap((order) =>
                    order.orders.map((item) => ({
                        sales_order: item.sales_order,
                        delivery_date: item.delivery_date,
                        po_numbers: item.po_numbers,
                        fee: item.fee,
                        tax: item.tax
                    }))
                );
            setCart(newCart);
        } else {
            setSelectedOrders([]);
            setCart([]);
        }
    }, [openOrders]);

    // ====================== AR BALANCE WARNING ======================
    // useEffect(() => {
    //     if (userData?.ar_balance > 0.00) {
    //         const title = 'AR Balance Warning';
    //         const message = (
    //             <>
    //                 You have an outstanding AR balance of{' '}
    //                 <span style={{ fontWeight: 'bold' }}>${userData.ar_balance}</span>. All orders
    //                 will remain on delivery block until balance has been paid in full via online banking bill payment.
    //             </>
    //         );
    //         setPopupMessage(message);
    //         setPopupTitle(title);
    //         setPopupIcon(warningIcon);
    //         setInfoPopupVisible(true);
    //     }
    // }, [userData]);

    // ====================== PRODUCT HISTORY (when expanded) ======================
    useEffect(() => {
        if (!isExpanded || productHistory.length > 0) return;
        if (adminUser && !viewedUser) return;

        const userId = adminUser ? viewedUser.user_id : user?.id;
        if (!userId) return;

        const fetchProductHistory = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`/api/productHistory?userId=${userId}`);
                const data = await response.json();
                setProductHistory(data);
            } catch (error) {
                console.error('Error fetching product history:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProductHistory();
    }, [isExpanded, adminUser, viewedUser?.user_id, user?.id]);

    // ====================== HELPER FUNCTIONS ======================
    const fetchOrderHistory = async (userId, page = 1, limit = 10, search = '') => {
        const params = new URLSearchParams({ userId, page, limit });
        if (search) params.set('search', search);
        const res = await fetch(`/api/orderHistory?${params}`);
        if (!res.ok) throw new Error('Failed to fetch order history');
        return await res.json();
    };

    const fetchTransactionHistory = async (userId, page = 1, limit = 10) => {
        const params = new URLSearchParams({ userId, page, limit });
        const res = await fetch(`/api/transactionHistory?${params}`);
        if (!res.ok) throw new Error('Failed to fetch transaction history');
        return await res.json();
    };

    const getTransactionDetails = async (cartId) => {
        const queryParams = new URLSearchParams({ cartId }).toString();
        const response = await fetch(`/api/transactionDetails?${queryParams}`);
        if (!response.ok) throw new Error('Failed to fetch transaction details');
        return await response.json();
    };

    const updateProductLists = async (userId) => {
        try {
            const [newActiveProducts, newAvailableProducts] = await Promise.all([
                fetch(`/api/activeProducts?userId=${userId}&date=${new Date().toISOString().split('T')[0]}`).then(r => r.json()),
                fetch(`/api/products?userId=${userId}&date=${new Date().toISOString().split('T')[0]}`).then(r => r.json())
            ]);

            const filteredAvailableProducts = newAvailableProducts.filter(product =>
                !newActiveProducts.some(p => p.product_id === product.product_id)
            );

            setProducts(filteredAvailableProducts);
            setActiveProducts(newActiveProducts);
        } catch (error) {
            console.error('Error updating product lists:', error);
        }
    };

    const handlePayment = async () => {
        if (cart.length === 0) {
            setMessage('No orders selected for payment.');
            return;
        }

        const totalAmount = calculateTotal().toFixed(2);
        const payload = {
            cart: cart.map(item => ({ sales_order: item.sales_order, fee: item.fee })),
            amount: parseFloat(totalAmount),
            billing_name: userData.user_name,
            address: userData.address,
            city: userData.city,
            province: userData.province,
            country: 'CA',
            postal_code: userData.postal_code,
            email: userData.email,
        };

        try {
            setIsLoading(true);
            const response = await fetch('/api/processPayment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error('Failed to process payment');

            const data = await response.json();
            if (data && data.pageLink) {
                window.location.href = data.pageLink;
            } else {
                setMessage('Payment initiation failed: No payment link provided.');
            }
        } catch (error) {
            console.error('Payment error:', error);
            setMessage('Error processing payment: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEnrollment = (product, actionTypeParam) => {
        setEnrollmentAck(false);
        setServiceToAction(product);
        setActionType(actionTypeParam);
        setServiceConfirmVisible(true);
    };

    const handleUserConfirmation = async (product, actionTypeParam) => {
        if (!enrollmentAck) return;

        setIsLoading(true);
        setServiceConfirmVisible(false);

        const user_id = user?.id || viewedUser?.user_id;
        const product_id = product.product_id;
        const startDt = product.startDt || new Date().toISOString().split('T')[0];
        const endDt = actionTypeParam !== 1 ? new Date().toISOString().split('T')[0] : null;

        try {
            const response = await fetch('/api/enroll-service', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user_id,
                    productId: product_id,
                    actionType: actionTypeParam,
                    startDt,
                    endDt
                })
            });

            const result = await response.json();
            if (!result.success) throw new Error(result.error || 'Operation failed');

            await updateProductLists(user_id);

            setPopupTitle('Enrollment Complete');
            setPopupMessage(result.message);
            setPopupIcon(successIcon);
            setInfoPopupVisible(true);
        } catch (error) {
            console.error('Enrollment error:', error);
            setPopupTitle('Enrollment Error');
            setPopupMessage(error.message || 'Failed to update service');
            setPopupIcon(errorIcon);
            setInfoPopupVisible(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTransHistory = async (order) => {
        const transactionId = order.transaction_id;
        const cartId = order.cart_id;
        const isRowExpanded = expandedItems.includes(transactionId);

        if (!isRowExpanded) {
            try {
                const expandedData = await getTransactionDetails(cartId);
                setExpandedHistory(prev => ({ ...prev, [transactionId]: expandedData }));
            } catch (error) {
                console.error('Error fetching transaction details:', error);
            }
        }

        setExpandedItems(
            isRowExpanded
                ? expandedItems.filter(id => id !== transactionId)
                : [...expandedItems, transactionId]
        );
    };

    // ====================== UTILITY FUNCTIONS ======================
    const calculateTotal = () => {
        return cart.reduce((sum, item) => sum + (item.fee || 0) + (item.tax || 0), 0);
    };

    const groupOrdersBySalesOrder = (orders) => {
        const grouped = orders.reduce((acc, order) => {
            const { sales_order, fee, tax } = order;
            if (!acc[sales_order]) {
                acc[sales_order] = {
                    sales_order,
                    totalFee: 0,
                    totalTax: 0,
                    po_numbers: order.po_numbers || '',   // ← plural string
                    delivery_date: order.delivery_date,
                    orders: [],
                };
            }
            acc[sales_order].totalFee += fee || 0;
            acc[sales_order].totalTax += tax || 0;
            acc[sales_order].orders.push(order);
            return acc;
        }, {});
        return Object.values(grouped);
    };

    const groupByPoNumber = (items) => {
        return Object.values(
            items.reduce((acc, item) => {
                const { po_numbers, sales_order, fee, tax, product_name } = item;
                const key = po_numbers || sales_order; // fallback to sales_order if no POs
                if (!acc[key]) {
                    acc[key] = {
                        po_numbers: key,
                        sales_orders: new Set(),
                        items: [],
                        totalFee: 0,
                        totalTax: 0,
                    };
                }
                acc[key].sales_orders.add(sales_order);
                acc[key].items.push({ product_name, fee, tax });
                acc[key].totalFee += Number(fee) || 0;
                acc[key].totalTax += Number(tax) || 0;
                return acc;
            }, {})
        );
    };

    const formatDateTime = (datetime) => {
        if (!datetime) return '';
        return new Date(datetime).toLocaleString('en-US', { dateStyle: 'medium' });
    };

    const formatDate = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('en-US', { dateStyle: 'medium' });
    };

    const formatStatus = (statusId) => {
        if (statusId === 2 || statusId === 4) return 'positive';
        if (statusId === 3) return 'negative';
        return '';
    };

    const formatStatusIcon = (statusId) => {
        if (statusId === 1) return unpaidIcon;
        if (statusId === 2 || statusId === 4) return paidIcon;
        if (statusId === 3) return cancelledIcon;
        return '';
    };

    const productCancelVisible = (product) => {
        if (!product.startDt) return false;
        const today = new Date();
        const startDt = new Date(product.startDt);
        const computedDate = startDt.getTime() + (product.min_enrollment || 0) * 24 * 60 * 60 * 1000;
        return today >= new Date(computedDate);
    };

    const myServicesText = (textId) => {
        if (adminUser) {
            return viewedUser
                ? <p>No services are currently active.</p>
                : <p>Select a customer to view the services they are enrolled in.</p>;
        }
        if (textId === 'orderHistory') return <p>Your past orders including enrolled services will be displayed here.</p>;
        if (textId === 'transactionHistory') return <p>Once payments have been completed, they will be displayed here.</p>;
        if (textId === 'myServices') return <p>Browse the list of available services. Once enrolled, your active services will be displayed here.</p>;
        return null;
    };

    const availableServicesText = () => {
        if (adminUser) {
            return viewedUser
                ? <p>No additional services available for enrollment.</p>
                : <p>Select a customer to view the services available to them.</p>;
        }
        return <p>As services are made available, they will be shown here.</p>;
    };

    const handleOrderSelection = (salesOrder, isChecked) => {
        setSelectedOrders((prev) => {
            const updatedOrders = isChecked
                ? [...prev, salesOrder.sales_order]
                : prev.filter((order) => order !== salesOrder.sales_order);

            const groupedOrders = groupOrdersBySalesOrder(openOrders);
            const newCart = groupedOrders
                .filter((order) => updatedOrders.includes(order.sales_order))
                .flatMap((order) =>
                    order.orders.map((item) => ({
                        sales_order: item.sales_order,
                        delivery_date: item.delivery_date,
                        po_numbers: item.po_numbers,  
                        fee: item.fee,
                        tax: item.tax
                    }))
                );

            setCart(newCart);
            return updatedOrders;
        });
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setOrderPage(1);
    };

    const handleTransactionClick = (transactionId) => {
        setSelectedTransactionId(transactionId);
    };

    const clearTransactionFilter = () => {
        setSelectedTransactionId(null);
    };

    const closePopup = () => {
        setServiceConfirmVisible(false);
        setInfoPopupVisible(false);
    };

    const closePasswordPopup = () => {
        setPasswordServiceVisible(false);
        setIsFirstTime(false);

        setPopupTitle('Password Updated');
        setPopupMessage('Your new password has been set and your security questions and responses have been saved.');
        setPopupIcon(warningIcon);
        setInfoPopupVisible(true);
    };

    // ====================== RENDER ======================
    const filteredOrderHistory = orderHistory?.filter((order) =>
        (order.po_numbers ?? '').toString().toLowerCase().includes(
            (searchQuery ?? '').toString().toLowerCase()
        )
    );

    const filteredTransactionHistory = selectedTransactionId
        ? transactionHistory?.filter((transaction) => transaction.transaction_id === selectedTransactionId)
        : transactionHistory;

    return (
        <div className="page-content">
            {!isLoading && (
                <>
                    {/* OUTSTANDING ORDERS + AVAILABLE SERVICES */}
                    <div className="panels">
                        <div className="leftPanel">
                            <div className="leftPanelContent">
                                <h1>OUTSTANDING ORDERS</h1>
                                {openOrders && openOrders.length > 0 ? (
                                    <>
                                        <table className="ordersTable">
                                            <thead>
                                                <tr>
                                                    <th>ORDER</th>
                                                    <th>DELIVERY DATE</th>
                                                    <th className="right">AMOUNT OWING</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupOrdersBySalesOrder(openOrders).map((order) => (
                                                    <React.Fragment key={order.sales_order}>
                                                        <tr className="salesOrderRow">
                                                            <td className="orderFeeLine">{order.po_numbers
                                                                ? order.po_numbers.split(', ').map((po, i) => (
                                                                    <div key={i}>{po}</div>
                                                                ))
                                                                : '—'}</td>
                                                            <td className='orderFeeLine'>{formatDate(order.delivery_date)}</td>
                                                            <td className="right orderFeeLine">${order.totalFee.toFixed(2)}</td>
                                                            <td className="rightInput">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedOrders.includes(order.sales_order)}
                                                                    onChange={(e) => handleOrderSelection(order, e.target.checked)}
                                                                />
                                                            </td>
                                                        </tr>
                                                        {order.orders.map((item, index) => (
                                                            <tr className="orderDetailsRow" key={`${order.sales_order}-${index}`}>
                                                                <td className="feeLine">{item.product_name}</td>
                                                                <td></td>
                                                                <td className="right">${item.fee.toFixed(2)}</td>
                                                                <td></td>
                                                            </tr>
                                                        ))}
                                                        <tr className="orderDetailsRow">
                                                            <td className="feeLine tax-label">Tax</td>
                                                            <td></td>
                                                            <td className="right">${Number(order.totalTax).toFixed(2)}</td>
                                                            <td></td>
                                                        </tr>
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div className="balances">
                                            <div className="balanceBox">
                                                <div className="balanceDiv">
                                                    <p>Outstanding Balance:</p>
                                                    <p className="balance">${calculateTotal().toFixed(2)}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            className={`actionButton ${adminUser ? "hidden-div" : ""} ${calculateTotal() > 0 ? "" : "hidden-div"}`}
                                            onClick={handlePayment}
                                        >
                                            PAY NOW
                                        </button>
                                    </>
                                ) : (
                                    <div className="panelInfoDiv">
                                        <img src={beerMug} className="panelIcons" alt="beer mug" />
                                        <p>No outstanding orders.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="rightPanel">
                            <h1>AVAILABLE SERVICES</h1>
                            {products && products.length > 0 ? (
                                products.map((product) => (
                                    <div className="products" key={product.product_id}>
                                        <div className="product-split-container">
                                            <div className='product-top'>
                                                <div className="product-left">
                                                    <h2>{product.product_name}</h2>
                                                    <p>{product.product_description}</p>
                                                    {product.rate_max && <p>*Maximum ${product.rate_max} per order</p>}
                                                </div>
                                                <div className="product-right">
                                                    <div className='product-badge'>
                                                        <div className='product-price'>
                                                            <h1>
                                                                {product.rate_fixed ? `$${product.rate_fixed}` : `$${product.rate}`}
                                                            </h1>
                                                        </div>
                                                        <div className='price-desc'>
                                                            {product.rate_fixed ? `PER ORDER` : `PER CASE*`}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='product-bottom'>
                                                <button className='actionButton' onClick={() => handleEnrollment(product, 1)}>
                                                    ENROLL NOW
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="panelInfoDiv">
                                    <img src={beerMug} className="panelIcons" alt="beer mug" />
                                    {availableServicesText()}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ORDER HISTORY + TRANSACTION HISTORY + MY SERVICES + SERVICE HISTORY */}
                    <div className="panels">
                        {/* ORDER HISTORY PANEL */}
                        <div className="leftPanel">
                            <div className="leftPanelContent">
                                <div className='historyHeader'>
                                    <h1>ORDER HISTORY</h1>
                                    <input
                                        type="text"
                                        placeholder="Enter Order Number"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                    />
                                </div>

                                {ordersLoading ? (
                                    // Scenario 1: Loading → Placeholder with beer mug
                                    <div className="panelInfoDiv">
                                        <div className="panel-animation">
                                            <img src={beerLoader} alt="loading" className="loadingAnimation" />
                                        </div>
                                    </div>
                                ) : !filteredOrderHistory || filteredOrderHistory.length === 0 ? (
                                    // Scenario 2: Not loading + No data
                                    <div className="panelInfoDiv">
                                        <img src={beerMug} className="panelIcons" alt="beer mug" />
                                        {myServicesText('orderHistory')}
                                    </div>
                                ) : (
                                    // Scenario 3: Not loading + Has data → Show table + Pagination
                                    <>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>ORDER</th>
                                                    <th>DELIVERY DATE</th>
                                                    <th className="center">STATUS</th>
                                                    <th className="center">TRANSACTION</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredOrderHistory.map((order) => (
                                                    <tr key={order.po_number}>
                                                        <td>{order.po_numbers
                                                            ? order.po_numbers.split(', ').map((po, i) => (
                                                                <div key={i}>{po}</div>
                                                            ))
                                                            : '—'}</td>
                                                        <td>{formatDate(order.delivery_date)}</td>
                                                        <td className={formatStatus(order.status_id)}>
                                                            <img
                                                                src={formatStatusIcon(order.status_id)}
                                                                alt="status icon"
                                                            />
                                                        </td>
                                                        <td
                                                            className="center"
                                                            onClick={() => handleTransactionClick(order.transaction_id)}
                                                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                                                        >
                                                            {order.transaction_id}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <Pagination
                                            currentPage={orderPage}
                                            totalItems={orderPagination.totalItems}
                                            pageSize={ORDER_PAGE_SIZE}
                                            onPageChange={setOrderPage}
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        {/* TRANSACTION HISTORY PANEL */}
                        <div className="rightPanel">
                            <div className="historyHeader">
                                <h1>TRANSACTION HISTORY</h1>
                                {selectedTransactionId && (
                                    <button onClick={clearTransactionFilter} className="clearFilterButton">
                                        CLEAR FILTER
                                    </button>
                                )}
                            </div>

                            {transactionsLoading ? (
                                // Scenario 1: Loading Animation
                                <div className="panelInfoDiv">
                                    <div className="panel-animation">
                                        <img src={beerLoader} alt="loading" className="loadingAnimation" />
                                    </div>
                                </div>
                            ) : !filteredTransactionHistory || filteredTransactionHistory.length === 0 ? (
                                // Scenario 2: Not loading + No data 
                                <div className="panelInfoDiv">
                                    <img src={beerMug} className="panelIcons" alt="beer mug" />
                                    {myServicesText('transactionHistory')}
                                </div>
                            ) : (
                                // Scenario 3: Not loading + Has data → Show table + Pagination
                                <>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>TRANSACTION</th>
                                                <th>TRANSACTION DATE</th>
                                                <th className="right">TOTAL PAID</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTransactionHistory.map((order) => (
                                                <React.Fragment key={order.transaction_id}>
                                                    <tr>
                                                        <td>{order.transaction_id}</td>
                                                        <td>{formatDateTime(order.payment_DTM)}</td>
                                                        <td className="right">
                                                            ${order.amount.toFixed(2) || '0.00'}
                                                        </td>
                                                        <td
                                                            className="center"
                                                            onClick={() => handleTransHistory(order)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {expandedItems.includes(order.transaction_id) ? (
                                                                <img src={contractIcon} alt="contract" />
                                                            ) : (
                                                                <img src={expandIcon} alt="expand" />
                                                            )}
                                                        </td>
                                                    </tr>

                                                    {expandedItems.includes(order.transaction_id) && (
                                                        <tr className="expanded-row">
                                                            <td colSpan="4">
                                                                <div className="expandedContent">
                                                                    {groupByPoNumber(expandedHistory[order.transaction_id] || []).map((group) => (
                                                                        <div key={group.po_number} className="po-group">
                                                                            <div className="left-column">
                                                                                <div className="po-number">{group.po_numbers
                                                                                    ? group.po_numbers.split(', ').map((po, i) => (
                                                                                        <div key={i}>{po}</div>
                                                                                    ))
                                                                                    : '—'}</div>
                                                                                {group.items.map((item, index) => (
                                                                                    <div key={`${group.po_number}-${index}`} className='feeLine'>
                                                                                        {item.product_name}
                                                                                    </div>
                                                                                ))}
                                                                                <div className="tax-label feeLine">Tax</div>
                                                                                <div className="order-total-label">Order Total</div>
                                                                            </div>
                                                                            <div className="spacer-column"></div>
                                                                            <div className="right-column">
                                                                                <div className="empty-row"></div>
                                                                                {group.items.map((item, index) => (
                                                                                    <div key={`${group.po_number}-${index}`} className="right">
                                                                                        ${Number(item.fee).toFixed(2)}
                                                                                    </div>
                                                                                ))}
                                                                                <div className="right">${Number(group.totalTax).toFixed(2)}</div>
                                                                                <div className="right bold">
                                                                                    ${(group.totalFee + group.totalTax).toFixed(2)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>

                                    <Pagination
                                        currentPage={transactionPage}
                                        totalItems={transactionPagination.totalItems}
                                        pageSize={TRANSACTION_PAGE_SIZE}
                                        onPageChange={setTransactionPage}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* MY SERVICES */}
                    <div className="bottomPanel">
                        <h1>MY SERVICES</h1>
                        {activeProducts && activeProducts.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        <th>SERVICE</th>
                                        <th>EFFECTIVE DATE</th>
                                        <th>{adminUser ? "END DATE" : ""}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeProducts.map((product) => (
                                        <tr key={product.id || product.product_name}>
                                            <td>{product.product_name}</td>
                                            <td>{formatDate(product.startDt)}</td>
                                            {adminUser ? (
                                                <td>{formatDate(product.endDt)}</td>
                                            ) : (
                                                <td className="rightInput">
                                                    {product.endDt && <p className="negative">Ending {formatDate(product.endDt)}</p>}
                                                    {productCancelVisible(product) && (
                                                        <button onClick={() => handleEnrollment(product, 2)}>CANCEL</button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="panelInfoDiv">
                                <img src={beerMug} className="panelIcons" alt="beer mug" />
                                {myServicesText('myServices')}
                            </div>
                        )}
                    </div>

                    {/* SERVICE HISTORY */}
                    <div className="bottomPanel">
                        <div className="bottomPanelHeader">
                            <div></div>
                            <h1>SERVICE HISTORY</h1>
                            <button onClick={() => setIsExpanded(!isExpanded)} className="expandButton">
                                {isExpanded ? <img src={contractIcon} alt="contract" /> : <img src={expandIcon} alt="expand" />}
                            </button>
                        </div>
                        {isExpanded && (
                            <table>
                                <thead>
                                    <tr>
                                        <th>SERVICE</th>
                                        <th>EFFECTIVE DATE</th>
                                        <th>END DATE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productHistory.map((product) => (
                                        <tr key={product.id || product.product_name}>
                                            <td>{product.product_name}</td>
                                            <td>{formatDate(product.startDt)}</td>
                                            <td>{formatDate(product.endDt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}

            {/* LOADING OVERLAY */}
            {isLoading && (
                <div className="overlay">
                    <div className="animationContainer">
                        <img src={beerLoader} alt="loading" className="loadingAnimation" />
                    </div>
                </div>
            )}

            {/* POPUPS */}
            {serviceConfirmVisible && (
                <div className="popup-container">
                    <div className="popup-overlay" />
                    <div className="popup-content">
                        <ActionService
                            onClose={closePopup}
                            onConfirm={handleUserConfirmation}
                            product={serviceToAction}
                            actionType={actionType}
                            setEnrollmentAck={setEnrollmentAck}
                        />
                    </div>
                </div>
            )}

            {infoPopupVisible && (
                <div className="popup-container">
                    <div className="popup-overlay" />
                    <div className="popup-content">
                        <InfoPopup onConfirm={closePopup} title={popupTitle} message={popupMessage} icon={popupIcon} />
                    </div>
                </div>
            )}

            {passwordServiceVisible && (
                <div className="popup-container">
                    <div className="popup-overlay" />
                    <PasswordService onConfirm={closePasswordPopup} />
                </div>
            )}
        </div>
    );
}

export default Home;